import {Select, SelectContent, SelectItem,
    SelectTrigger, SelectValue} from "../ui/select";
import React, {useState} from "react";
import {ProjectContext, ProjectContextType} from "../../contexts/ProjectContext";
import {BaseScopeReference} from "../scope/ScopeModel";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {Button} from "../ui/button";
import {ChevronDown} from "lucide-react";

interface ScopeSelectorProps {
    handleSelect?: (s: string | null) => void;
    value?: string | null;
}


export function GetScope(projectContext: ProjectContextType | null, scope_id: string){
    return projectContext?.scopes.find(x => x._id === scope_id)
}

export function DefaultScope(projectContext: ProjectContextType | null){
    return ( projectContext?.scopes && (projectContext?.scopes.length > 0))
        ? projectContext?.scopes.at(0) : null
}

export function ScopesSelector(
    {
        value,
        handleSelect
    }:ScopeSelectorProps
){
    const projectContext = React.useContext(ProjectContext);

    const onValueChange = (val: string) => {
        if (handleSelect) {
            handleSelect(val === "__all__" ? null : val);
        }
    };

    return <Select onValueChange={onValueChange} value={value ?? "__all__"}>
          <SelectTrigger>
            <SelectValue placeholder="Select Scope" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem key="__all__" value="__all__">All Scopes</SelectItem>
            <SelectItem key="__none__" value="__none__">No Scope</SelectItem>
            {projectContext?.scopes.map((scope) => (
              <SelectItem key={scope._id} value={scope._id}>
                {scope.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
}


interface ScopeMultiSelectProps {
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
}

export function ScopeMultiSelect({
    selected,
    onChange,
    placeholder = "Select Scopes",
}: ScopeMultiSelectProps) {
    const projectContext = React.useContext(ProjectContext);
    const scopes = projectContext?.scopes ?? [];

    const toggleScope = (scopeId: string) => {
        if (selected.includes(scopeId)) {
            onChange(selected.filter(s => s !== scopeId));
        } else {
            onChange([...selected, scopeId]);
        }
    };

    const selectAll = () => {
        onChange(scopes.map(s => s._id));
    };

    const selectNone = () => {
        onChange([]);
    };

    const label = selected.length === 0
        ? placeholder
        : selected.length === scopes.length
            ? "All Scopes"
            : `${selected.length} scope${selected.length > 1 ? "s" : ""} selected`;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between">
                    <span className="truncate">{label}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Scopes</DropdownMenuLabel>
                <DropdownMenuSeparator/>
                <DropdownMenuCheckboxItem
                    checked={selected.length === scopes.length && scopes.length > 0}
                    onCheckedChange={(checked) => checked ? selectAll() : selectNone()}
                >
                    All Scopes
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator/>
                {scopes.map((scope) => (
                    <DropdownMenuCheckboxItem
                        key={scope._id}
                        checked={selected.includes(scope._id)}
                        onCheckedChange={() => toggleScope(scope._id)}
                    >
                        {scope.title}
                    </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
